/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2023 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2023 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { createSelector } from '@reduxjs/toolkit';
import type { SystemScope } from '@opengeoweb/shared';
import { selectorMemoizationOptions } from '@opengeoweb/store';
import type { AppStore } from '../types';
import { ADMIN_WORKSPACE_FILTER_IDS } from './reducer';
import type {
  WorkspaceActionDialogType,
  WorkspaceDialogView,
  WorkspaceListState,
} from './types';
import { PresetFilterType } from './types';
import { getWorkspaceState } from '../workspace/selectors';
import type { WorkspacePresetLabels, WorkspaceState } from '../workspace/types';

export const getWorkspaceListStore = (store: AppStore): WorkspaceListState => {
  if (store?.workspaceList) {
    return store.workspaceList;
  }
  return null!;
};

export const getWorkspaceDialogIsSaved = createSelector(
  getWorkspaceListStore,
  (store: AppStore) => store,
  (state: WorkspaceListState) => {
    return state?.workspaceActionDialog?.hasSaved ?? false;
  },
  selectorMemoizationOptions,
);

export const getWorkspaceListError = createSelector(
  getWorkspaceListStore,
  (store: AppStore) => store,
  (state: WorkspaceListState) => {
    return state?.error ?? undefined;
  },
  selectorMemoizationOptions,
);

export const getWorkspaceActionFormValues = createSelector(
  getWorkspaceListStore,
  (store) => store.workspaceActionDialog?.formValues ?? {},
  selectorMemoizationOptions,
);

type WorkspaceActionDialogDetails = WorkspaceActionDialogType | false;

export const getWorkspaceActionDialogDetails = createSelector(
  getWorkspaceListStore,
  getWorkspaceState,
  (
    workspaceListState: WorkspaceListState,
    workspaceState: WorkspaceState,
  ): WorkspaceActionDialogDetails => {
    const dialog = workspaceListState?.workspaceActionDialog ?? false;
    if (dialog) {
      const views = (dialog.formValues.views as WorkspaceDialogView[])?.map(
        (view): WorkspaceDialogView => {
          const workspaceView =
            workspaceState?.views?.byId?.[view.mosaicNodeId];

          if (!workspaceView) {
            return view;
          }

          return {
            ...view,
            mosaicNodeId: view.mosaicNodeId,
            title: view.title || workspaceView.title || view.mosaicNodeId,
            abstract: workspaceView.abstract,
            componentType: workspaceView.componentType ?? view.componentType,
            scope: view.scope || workspaceView.scope || 'system',
            initialProps: view.initialProps ?? workspaceView.initialProps,
            originalViewPresetId: view?.originalViewPresetId,
          };
        },
      );
      return {
        ...dialog,
        formValues: { ...dialog.formValues, ...(views && { views }) },
      };
    }
    return dialog;
  },
  selectorMemoizationOptions,
);

export const getIsWorkspaceListDialogOpen = createSelector(
  getWorkspaceListStore,
  (store: AppStore) => store,
  (state: WorkspaceListState) => {
    return state?.isWorkspaceListDialogOpen || false;
  },
  selectorMemoizationOptions,
);

export const getWorkspaceListFilters = createSelector(
  (store: AppStore) => getWorkspaceListStore(store),
  (_store: AppStore, scope?: SystemScope) => scope,
  (_store: AppStore, _scope?: SystemScope, isAdmin?: boolean) => isAdmin,
  (
    _store: AppStore,
    _scope?: SystemScope,
    _isAdmin?: boolean,
    isTemplatesEnabled?: boolean,
  ) => isTemplatesEnabled !== false,
  (
    state: WorkspaceListState,
    _scope: SystemScope | undefined,
    isAdmin: boolean | undefined,
    isTemplatesEnabled: boolean,
  ) => {
    const filters = (state?.workspaceListFilters ?? []).filter(
      (filter) =>
        isTemplatesEnabled || filter.id !== PresetFilterType.templates,
    );

    if (isAdmin) {
      // Only return system, shared, template and favourite filters for admins
      return filters
        .filter((filter) => ADMIN_WORKSPACE_FILTER_IDS.includes(filter.id))
        .map((filter) => ({
          ...filter,
          isDisabled: false,
        }));
    }

    // Regular users: return all filters
    return filters.map((filter) => ({
      ...filter,
      isDisabled: false,
    }));
  },
  selectorMemoizationOptions,
);

export const getWorkspaceListSearchQuery = createSelector(
  getWorkspaceListStore,
  (store: AppStore) => store,
  (state: WorkspaceListState) => {
    return state?.searchQuery ?? '';
  },
  selectorMemoizationOptions,
);

export const getWorkspaceLabelFilters = createSelector(
  getWorkspaceListStore,
  (store: AppStore) => store,
  (state: WorkspaceListState): WorkspacePresetLabels | undefined => {
    return state.workspaceLabelFilters;
  },
  selectorMemoizationOptions,
);
